/**
 * BUNDLE SUBSCRIPTION WIDGET
 *
 * Assigned onto `window` rather than declared, because this file is loaded from
 * inside the main-product <section>: quick-add.js re-injects every script it
 * finds there (quick-add.js:150-159), and a second top-level `class` declaration
 * would throw a redeclaration SyntaxError. `x = x || class {}` re-executes
 * harmlessly.
 */
window.SubscriptionWidgetV2 =
  window.SubscriptionWidgetV2 ||
  class SubscriptionWidgetV2 {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.subscribeRadio = document.getElementById(`subscribe-option-v2-${sectionId}`);
    this.onetimeRadio = document.getElementById(`onetime-option-v2-${sectionId}`);
    this.frequencySection = document.getElementById(`frequency-section-v2-${sectionId}`);
    this.sellingPlanInput = document.getElementById(`selling-plan-input-v2-${sectionId}`);
    this.frequencyToggle = this.frequencySection?.querySelector('.subscription-widget-v2__frequency-toggle');
    this.frequencyDropdown = this.frequencySection?.querySelector('.subscription-widget-v2__frequency-dropdown');
    this.frequencyOptions = this.frequencySection?.querySelectorAll(`input[name="selling-plan-v2-${sectionId}"]`);
    this.selectedFrequencyText = document.getElementById(`selected-frequency-v2-${sectionId}`);
    
    this.productForm = document.getElementById(`product-form-${sectionId}`);
    this.variantIdInput = this.productForm?.querySelector('input[name="id"]');
    this.currentVariantPrice = 0;
    this.discountPercent = this.getDiscountPercent();
    
    this.setupButtonStructure();
    this.init();
  }
  
  init() {
    if (!this.subscribeRadio || !this.onetimeRadio) return;
    
    this.subscribeRadio.addEventListener('change', () => this.updatePurchaseOption());
    this.onetimeRadio.addEventListener('change', () => this.updatePurchaseOption());
    
    if (this.frequencyToggle) {
      this.frequencyToggle.addEventListener('click', () => this.toggleFrequencyDropdown());
    }
    
    if (this.frequencyOptions) {
      this.frequencyOptions.forEach(option => {
        option.addEventListener('change', (e) => this.selectFrequency(e.target));
      });
    }
    
    if (this.variantIdInput) {
      this.watchVariantChanges();
    }
    
    this.watchQuantityChanges();
    this.watchButtonChanges();
    
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
    
    this.updatePurchaseOption();
  }
  
  setupButtonStructure() {
    const button = this.productForm?.querySelector('.product-form__submit');
    if (!button) {
      console.warn('⚠️ Add to cart button not found');
      return;
    }
    
    const buttonLabel = button.querySelector('.button__label');
    if (!buttonLabel) {
      console.warn('⚠️ Button label not found');
      return;
    }
    
    const existingPriceElement = buttonLabel.querySelector('.button__label-price');
    if (existingPriceElement) {
      console.log('✅ Button structure already exists');
      this.buttonPriceElement = existingPriceElement;
      
      if (existingPriceElement.dataset.variantPrice) {
        this.currentVariantPrice = parseInt(existingPriceElement.dataset.variantPrice);
        console.log('   Stored price:', this.currentVariantPrice);
      }
      
      return;
    }
    
    const currentText = buttonLabel.textContent.trim();
    console.log('Setting up button, text:', currentText);
    
    const priceMatch = currentText.match(/[-–]\s*(£[\d,]+\.?\d*)/);
    
    if (priceMatch) {
      const fullPriceText = priceMatch[0].trim();
      const priceOnly = priceMatch[1];
      const labelText = currentText.replace(fullPriceText, '').trim();
      
      const priceValue = parseFloat(priceOnly.replace('£', '').replace(',', ''));
      const priceInCents = Math.round(priceValue * 100);
      
      this.currentVariantPrice = priceInCents;
      
      console.log('Extracted price:', priceOnly, '=', priceInCents, 'cents');
      
      buttonLabel.innerHTML = `
        <span class="button__label-text">${labelText}</span>
        <span class="button__label-separator"> - </span>
        <span class="button__label-price" data-variant-price="${priceInCents}">${priceOnly}</span>
      `;
      
      this.buttonPriceElement = buttonLabel.querySelector('.button__label-price');
      console.log('✅ Button structure created');
      
    } else if (this.currentVariantPrice > 0) {
      console.log('No price in button, using stored price:', this.currentVariantPrice);
      
      const labelText = currentText.trim();
      const priceText = this.formatMoney(this.currentVariantPrice);
      
      buttonLabel.innerHTML = `
        <span class="button__label-text">${labelText}</span>
        <span class="button__label-separator"> - </span>
        <span class="button__label-price" data-variant-price="${this.currentVariantPrice}">${priceText}</span>
      `;
      
      this.buttonPriceElement = buttonLabel.querySelector('.button__label-price');
      console.log('✅ Button structure restored with stored price');
      
    } else {
      console.warn('⚠️ Could not extract price and no stored price available');
    }
  }
  
  watchButtonChanges() {
    const button = this.productForm?.querySelector('.product-form__submit');
    if (!button) return;
    
    let mutationTimeout;
    
    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimeout);
      
      mutationTimeout = setTimeout(() => {
        const buttonLabel = button.querySelector('.button__label');
        const hasPriceStructure = buttonLabel?.querySelector('.button__label-price');
        
        if (buttonLabel && !hasPriceStructure) {
          console.log('⚠️ Button HTML replaced');
          this.setupButtonStructure();
          setTimeout(() => this.updateButtonPrice(), 10);
        }
      }, 50);
    });
    
    observer.observe(button, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }
  
  getDiscountPercent() {
    const widgetContainer = document.querySelector('.subscription-widget-v2');
    if (widgetContainer) {
      const product = widgetContainer.closest('[data-section]');
      if (product) {
        const sellingPlanGroups = window.productSellingPlanGroups;
        if (sellingPlanGroups && sellingPlanGroups.length > 0) {
          const firstPlan = sellingPlanGroups[0].selling_plans[0];
          if (firstPlan && firstPlan.price_adjustments && firstPlan.price_adjustments.length > 0) {
            const adjustment = firstPlan.price_adjustments[0];
            if (adjustment.value_type === 'percentage') {
              return parseInt(adjustment.value);
            }
          }
        }
      }
    }
    return 0;
  }

  getSubscriptionPrice() {
    const priceInPounds = this.currentVariantPrice / 100;
    const discountMultiplier = (100 - this.discountPercent) / 100;
    const subscriptionPriceInPounds = priceInPounds * discountMultiplier;
    return Math.round(subscriptionPriceInPounds * 100);
  }
  
  watchVariantChanges() {
    const observer = new MutationObserver(() => {
      console.log('📦 Variant changed');
      
      setTimeout(() => {
        if (!this.subscribeRadio?.checked) {
          const button = this.productForm?.querySelector('.product-form__submit .button__label');
          if (button) {
            const buttonText = button.textContent;
            const priceMatch = buttonText.match(/£([\d,]+\.?\d*)/);
            
            if (priceMatch) {
              const priceValue = parseFloat(priceMatch[1].replace(',', ''));
              const newPrice = Math.round(priceValue * 100);
              
              if (newPrice !== this.currentVariantPrice) {
                console.log(`💰 New variant price: ${this.formatMoney(newPrice)}`);
                this.currentVariantPrice = newPrice;
              }
            }
          }
        } else {
          console.log('⏭️ Subscription active, skipping price extraction');
        }
        
        const hasStructure = this.buttonPriceElement && document.body.contains(this.buttonPriceElement);
        if (!hasStructure) {
          this.setupButtonStructure();
        }
        
        this.updateButtonPrice();
      }, 100);
    });
    
    observer.observe(this.variantIdInput, {
      attributes: true,
      attributeFilter: ['value']
    });
  }
  
  watchQuantityChanges() {
    const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    if (!quantityInput) return;
    
    quantityInput.addEventListener('change', () => {
      console.log('🔢 Quantity changed to:', quantityInput.value);
      this.updateButtonPrice();
    });
    
    quantityInput.addEventListener('input', () => {
      console.log('🔢 Quantity input to:', quantityInput.value);
      this.updateButtonPrice();
    });
    
    const quantityButtons = this.productForm?.querySelectorAll('.quantity__button');
    quantityButtons?.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => {
          console.log('🔢 Quantity changed to:', quantityInput.value);
          this.updateButtonPrice();
        }, 50);
      });
    });
  }
  
  updatePurchaseOption() {
    if (this.subscribeRadio.checked) {
      this.frequencySection.style.display = 'block';
      const selectedPlan = this.frequencySection.querySelector(`input[name="selling-plan-v2-${this.sectionId}"]:checked`);
      if (selectedPlan) {
        this.sellingPlanInput.value = selectedPlan.value;
      }
    } else {
      this.frequencySection.style.display = 'none';
      this.sellingPlanInput.value = '';
    }
    this.updateButtonPrice();
  }
  
  toggleFrequencyDropdown() {
    const isExpanded = this.frequencyToggle.getAttribute('aria-expanded') === 'true';
    this.frequencyToggle.setAttribute('aria-expanded', !isExpanded);
    this.frequencyDropdown.hidden = isExpanded;
  }
  
  selectFrequency(option) {
    this.selectedFrequencyText.textContent = option.dataset.planName;
    this.sellingPlanInput.value = option.value;
    this.frequencyDropdown.hidden = true;
    this.frequencyToggle.setAttribute('aria-expanded', 'false');
    this.updateButtonPrice();
  }
  
  updateButtonPrice() {
    if (!this.buttonPriceElement || !document.body.contains(this.buttonPriceElement)) {
      this.setupButtonStructure();
      if (!this.buttonPriceElement) return;
    }
    
    const variantPrice = this.currentVariantPrice;
    
    if (variantPrice === 0) {
      console.warn('⚠️ No variant price available');
      return;
    }
    
    const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    
    this.buttonPriceElement.dataset.variantPrice = variantPrice;
    
    const buttonLabel = this.productForm?.querySelector('.button__label');
    const labelText = this.buttonPriceElement?.previousElementSibling?.previousElementSibling?.textContent || 'Add to Basket';
    
    if (this.subscribeRadio?.checked && this.sellingPlanInput.value && this.discountPercent > 0) {
      const subscriptionPrice = this.getSubscriptionPrice();
      const totalPrice = subscriptionPrice * quantity;
      const priceFormatted = this.formatMoney(totalPrice);
      
      console.log(`🔄 Subscription: ${priceFormatted} (${quantity} × ${this.formatMoney(subscriptionPrice)})`);
      this.buttonPriceElement.textContent = priceFormatted;
      
      if (buttonLabel) {
        buttonLabel.setAttribute('data-label', `${labelText} - ${priceFormatted}`);
      }
    } else {
      const totalPrice = variantPrice * quantity;
      const priceFormatted = this.formatMoney(totalPrice);
      
      console.log(`🔄 Regular: ${priceFormatted} (${quantity} × ${this.formatMoney(variantPrice)})`);
      this.buttonPriceElement.textContent = priceFormatted;
      
      if (buttonLabel) {
        buttonLabel.setAttribute('data-label', `${labelText} - ${priceFormatted}`);
      }
    }
  }
  
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `£${amount}`;
  }
  
  handleOutsideClick(e) {
    if (this.frequencySection && !this.frequencySection.contains(e.target)) {
      if (this.frequencyDropdown) {
        this.frequencyDropdown.hidden = true;
      }
      if (this.frequencyToggle) {
        this.frequencyToggle.setAttribute('aria-expanded', 'false');
      }
    }
  }
  };

/**
 * Binds every not-yet-bound widget under `root`.
 *
 * Exposed on window so the quick-add drawer can initialise a widget that was
 * injected long after DOMContentLoaded. `data-widget-bound` is what makes it
 * safe to call repeatedly — same guard as quick-add.js's `data-accordion-bound`.
 */
window.initSubscriptionWidgets = function (root) {
  const scope = root || document;

  scope.querySelectorAll('.subscription-widget-v2:not([data-widget-bound])').forEach((widget) => {
    const sectionId = widget.closest('[data-section]')?.dataset.section;
    if (!sectionId) return;

    widget.setAttribute('data-widget-bound', 'true');
    new window.SubscriptionWidgetV2(sectionId);
  });
};

// Outside the class guard: a re-injected copy of this file still needs to run
// this, and by then DOMContentLoaded has long since fired.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.initSubscriptionWidgets());
} else {
  window.initSubscriptionWidgets();
}